/**
 * The call-off's destination summary — what the list, the detail header and
 * the carrier view show as its Destination (``CallOff.destination_name``).
 */
import type { WizardDelivery } from '../../../lib/api';

export type CallOffDestinationType = 'warehouse' | 'direct' | 'mixed';

/**
 * Summarise every distinct destination of a call-off's deliveries: warehouses
 * first, then direct drops, each name once. A load to two warehouses reads
 * "Warehouse A, Warehouse B" rather than only the first. A mixed load keeps
 * the "Mixed: " prefix the list and detail screens key on.
 *
 * The server fits a long list to the column ("A, B +3 more"), so no trimming
 * happens here.
 *
 * @param deliveries - the wizard's deliveries, in the order they were added
 * @param destinationType - the load's destination type
 * @returns the summary, or '' when no delivery has a name
 */
export function summariseDestinations(
  deliveries: Pick<WizardDelivery, 'type' | 'destinationName'>[],
  destinationType: CallOffDestinationType,
): string {
  const namesOf = (type: WizardDelivery['type']) => deliveries
    .filter((d) => d.type === type)
    .map((d) => d.destinationName)
    .filter(Boolean);
  const unique = Array.from(new Set([...namesOf('warehouse'), ...namesOf('direct')]));
  const joined = unique.join(', ');
  return destinationType === 'mixed' ? `Mixed: ${joined}` : joined;
}
